import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Global 401 → refresh interceptor -----------------------------------
// The backend issues 15-min access tokens and 7-day refresh tokens.
// When an authenticated request 401s, transparently POST /auth/refresh once
// and retry the original request. Concurrent 401s share a single refresh call.
let _refreshInFlight = null;

axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !original ||
      original._retried ||
      // Don't try to refresh these — they define the auth lifecycle itself
      /\/api\/auth\/(refresh|login|logout|register|me|forgot-password|reset-password)/.test(original.url || '')
    ) {
      return Promise.reject(error);
    }

    original._retried = true;
    try {
      if (!_refreshInFlight) {
        _refreshInFlight = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
          .finally(() => { _refreshInFlight = null; });
      }
      await _refreshInFlight;
      // Ensure the retry keeps the cookie
      original.withCredentials = true;
      return axios(original);
    } catch (refreshErr) {
      return Promise.reject(error);
    }
  }
);
// -------------------------------------------------------------------------

// Helper to format API error details
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, email, password, company) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { name, email, password, company },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isPartner: user?.role === 'partner'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
